import React, { useEffect, useId, useMemo, useState } from 'react';
import { ChevronDown, Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  appReleaseService,
  pickLocalizedReleaseChanges,
  PublicAppRelease,
} from '../../services/appReleaseService';
import { pickLocalized } from '../../utils/localizedContent';
import { useAppLocale } from '../../providers/LocaleProvider';
import { hasReleaseChangeDetails, ReleaseChangeGroups } from './ReleaseChangeGroups';
import { CollapsibleFadePanel } from './CollapsibleFadePanel';
import { formatAppDate } from '../../utils/localeDateFormat';

const ReleaseCard: React.FC<{
  release: PublicAppRelease;
  isLatest: boolean;
  defaultExpanded?: boolean;
}> = ({ release, isLatest, defaultExpanded = false }) => {
  const { t } = useTranslation('nav');
  const { locale, direction } = useAppLocale();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const detailsId = useId();
  const localizedChanges = pickLocalizedReleaseChanges(release.changes, release.changesFa, locale);
  const hasDetails = hasReleaseChangeDetails(localizedChanges);
  const title = pickLocalized(release.title, release.titleFa, locale);
  const summary = pickLocalized(release.summary, release.summaryFa, locale);

  return (
    <article
      className={`relative rounded-xl border text-start transition-colors ${
        isLatest
          ? 'border-amber-300/80 bg-amber-50/60 shadow-sm dark:border-amber-600/40 dark:bg-amber-950/20'
          : 'border-slate-200/80 bg-white/50 dark:border-slate-700/80 dark:bg-gray-900/40'
      }`}
      dir={direction}
    >
      <button
        type="button"
        onClick={() => hasDetails && setExpanded((v) => !v)}
        disabled={!hasDetails}
        aria-expanded={hasDetails ? expanded : undefined}
        aria-controls={hasDetails ? detailsId : undefined}
        className={`relative block w-full px-4 py-3 text-start ${
          hasDetails
            ? 'cursor-pointer transition-colors hover:bg-white/40 dark:hover:bg-gray-900/30'
            : 'cursor-default'
        }`}
      >
        <div className={hasDetails ? 'pe-9' : undefined}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-semibold text-white dark:bg-amber-600">
              v{release.versionLabel}
            </span>
            {isLatest && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                {t('releaseLatest')}
              </span>
            )}
            <time className="text-xs text-slate-500 dark:text-slate-400" dateTime={release.releasedAt}>
              {formatAppDate(release.releasedAt, locale, { year: 'numeric', month: 'short', day: 'numeric' })}
            </time>
          </div>
          {title && (
            <h4 className="mt-1.5 text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h4>
          )}
        </div>
        {hasDetails && (
          <span
            className="absolute end-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200/90 bg-white/95 text-slate-500 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-slate-400"
            aria-hidden
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform duration-200 ease-out ${expanded ? 'rotate-180' : ''}`}
            />
          </span>
        )}
        <p
          className="mt-1 w-full text-justify text-[10px] leading-snug text-slate-600 sm:text-[11px] dark:text-slate-300"
          style={{ textAlign: 'justify' }}
        >
          {summary}
        </p>
      </button>

      {hasDetails && (
        <CollapsibleFadePanel expanded={expanded} panelId={detailsId}>
            <div className="border-t border-slate-200/70 px-4 pb-3 pt-3 dark:border-slate-700/70">
              <ReleaseChangeGroups changes={localizedChanges} prose />
            </div>
        </CollapsibleFadePanel>
      )}
    </article>
  );
};

export interface AppReleaseHistoryPanelProps {
  className?: string;
  /** When true, fetches on mount. When false, waits until `active` becomes true. */
  active?: boolean;
}

export const AppReleaseHistoryPanel: React.FC<AppReleaseHistoryPanelProps> = ({
  className = '',
  active = true,
}) => {
  const { t } = useTranslation('nav');
  const { direction } = useAppLocale();
  const [releases, setReleases] = useState<PublicAppRelease[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!active || loaded) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await appReleaseService.getPublishedReleases();
      if (!cancelled) {
        setReleases(data);
        setLoading(false);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, loaded]);

  const visibleReleases = useMemo(
    () => (showAll ? releases : releases.slice(0, 6)),
    [releases, showAll]
  );

  if (loading) {
    return (
      <div className={`flex items-center justify-center gap-2 py-12 text-sm text-slate-500 dark:text-slate-400 ${className}`} aria-live="polite">
        <Loader2 className="h-5 w-5 animate-spin text-amber-600 dark:text-amber-400" />
        {t('releaseLoading')}
      </div>
    );
  }

  if (!releases.length) {
    return (
      <div className={`rounded-xl border border-dashed border-slate-300/80 bg-slate-50/80 px-6 py-10 text-center dark:border-slate-600/80 dark:bg-gray-900/40 ${className}`}>
        <Sparkles className="mx-auto mb-3 h-8 w-8 text-amber-500/80" aria-hidden />
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('releaseEmpty')}</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="relative space-y-4">
        <div
          className="pointer-events-none absolute bottom-2 start-3 top-2 w-px bg-gradient-to-b from-amber-400/70 via-slate-300/50 to-transparent dark:from-amber-500/50 dark:via-slate-600/40"
          aria-hidden
        />
        {visibleReleases.map((release, index) => (
          <div key={release.id} className="relative ps-8" dir={direction}>
            <span
              className={`absolute start-1.5 top-4 h-3 w-3 rounded-full ring-2 ring-white dark:ring-gray-900 ${
                index === 0 ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'
              }`}
              aria-hidden
            />
            <ReleaseCard release={release} isLatest={index === 0} defaultExpanded={index === 0} />
          </div>
        ))}
      </div>

      {releases.length > 6 && (
        <div className="mt-5 text-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-sm font-medium text-amber-800 underline-offset-2 transition-colors hover:underline dark:text-amber-300"
          >
            {showAll ? t('releaseShowFewer') : t('releaseViewAll', { count: releases.length })}
          </button>
        </div>
      )}
    </div>
  );
};

/** Lightweight probe for whether published releases exist (footer link visibility). */
export async function hasPublishedAppReleases(): Promise<boolean> {
  const data = await appReleaseService.getPublishedReleases(1);
  return data.length > 0;
}
